import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';

import { useCurrency } from '@/hooks/useCurrency';
import type { SafetyStockSimulateSampleResponse } from '@/services/safetyStockService';
import {
  deltaVariant,
  formatDays,
  formatSignedPercentFraction,
  formatUnits,
  toNumber,
} from '../utils/format';

export type SampleSimulateModalMode = 'confirm' | 'progress' | 'result' | 'error';

interface Props {
  open: boolean;
  mode: SampleSimulateModalMode;
  sampleSize: number;
  populationSize?: number | null;
  result?: SafetyStockSimulateSampleResponse | null;
  errorMessage?: string | null;
  onConfirm: () => void;
  onClose: () => void;
  onRetry: () => void;
}

function formatSignedUnits(
  value: string | number | null | undefined,
  locale: string,
): string {
  const n = toNumber(value);
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${formatUnits(n, locale)}`;
}

function formatSignedMoney(
  value: string | number | null | undefined,
  formatRounded: (v: number | string | null | undefined) => string,
): string {
  const n = toNumber(value);
  if (n == null) return '—';
  if (n === 0) return formatRounded(0);
  const prefix = n > 0 ? '+' : '-';
  return `${prefix}${formatRounded(Math.abs(n))}`;
}

function deltaTextClass(pct: number | null): string {
  const v = deltaVariant(pct);
  if (v === 'danger') return 'text-red-700';
  if (v === 'warning') return 'text-amber-700';
  if (pct != null && pct < 0) return 'text-emerald-700';
  if (pct != null && pct > 0) return 'text-sky-800';
  return 'text-gray-900';
}

export function SafetyStockSampleSimulateModal({
  open,
  mode,
  sampleSize,
  populationSize,
  result,
  errorMessage,
  onConfirm,
  onClose,
  onRetry,
}: Props) {
  const { t, i18n } = useTranslation();
  const { formatRounded, symbol } = useCurrency();
  const locale = i18n.language || 'es';

  if (!open) return null;

  const metrics = result?.metrics || {};
  const units = toNumber(metrics.safety_stock_units as string | number | null | undefined);
  const value = toNumber(metrics.safety_stock_value as string | number | null | undefined);
  const prevUnits = toNumber(
    metrics.previous_safety_stock_units as string | number | null | undefined,
  );
  const prevValue = toNumber(
    metrics.previous_safety_stock_value as string | number | null | undefined,
  );
  const deltaUnits = toNumber(metrics.delta_units as string | number | null | undefined);
  const deltaValue = toNumber(metrics.delta_value as string | number | null | undefined);
  const deltaValuePct = toNumber(
    metrics.delta_value_pct as string | number | null | undefined,
  );
  const daily = toNumber(metrics.daily_demand_units as string | number | null | undefined);

  const simDays = toNumber(
    (metrics.coverage_days_units ?? metrics.coverage_days_median) as
      | string
      | number
      | null
      | undefined,
  );
  const prevDays =
    prevUnits != null && daily != null && daily > 0 ? prevUnits / daily : null;
  const deltaDays =
    simDays != null && prevDays != null ? simDays - prevDays : null;

  const hasBaseline =
    (prevUnits != null && prevUnits > 0) || (prevValue != null && prevValue > 0);

  const actualSample = result?.sample_size ?? sampleSize;
  const population = result?.population_size ?? populationSize ?? null;

  const title =
    mode === 'confirm'
      ? t('safetyStock.parameters.sampleModal.confirmTitle')
      : mode === 'error'
        ? t('safetyStock.parameters.sampleModal.errorTitle')
        : mode === 'result'
          ? t('safetyStock.parameters.sampleModal.resultTitle')
          : t('safetyStock.parameters.sampleModal.progressTitle');

  const content = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={mode === 'progress' ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="safety-stock-sample-modal-title"
        className="relative w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2
              id="safety-stock-sample-modal-title"
              className="text-base font-semibold text-gray-900"
            >
              {title}
            </h2>
            {mode === 'confirm' ? (
              <p className="mt-1 text-sm text-gray-600">
                {t('safetyStock.parameters.sampleModal.confirmMessage', {
                  sample: sampleSize,
                  population: population ?? '—',
                })}
              </p>
            ) : mode === 'progress' ? (
              <p className="mt-1 text-sm text-gray-500">
                {t('safetyStock.parameters.sampleModal.progressHint')}
              </p>
            ) : null}
          </div>
          {mode !== 'progress' ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('safetyStock.apply.close')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="px-5 py-4">
          {mode === 'confirm' ? (
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-gray-700">
              <li>{t('safetyStock.parameters.sampleModal.confirmBulletStratified')}</li>
              <li>{t('safetyStock.parameters.sampleModal.confirmBulletCompare', { currencySymbol: symbol })}</li>
              <li>{t('safetyStock.parameters.sampleModal.confirmBulletNoPersist')}</li>
              <li>{t('safetyStock.parameters.sampleModal.confirmBulletEstimate')}</li>
            </ul>
          ) : null}

          {mode === 'progress' ? (
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <Loader2 className="h-5 w-5 animate-spin text-stocker-primary" />
              {t('safetyStock.parameters.sampleModal.progressDetail', {
                sample: sampleSize,
              })}
            </div>
          ) : null}

          {mode === 'error' ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMessage || t('safetyStock.parameters.sampleModal.errorFallback')}</span>
            </div>
          ) : null}

          {mode === 'result' && result ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-sm text-emerald-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t('safetyStock.parameters.sampleModal.resultScope', {
                    sample: actualSample,
                    population: population ?? '—',
                  })}
                </span>
              </div>

              {!hasBaseline ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {t('safetyStock.parameters.sampleModal.noBaseline')}
                </p>
              ) : null}

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('safetyStock.parameters.sampleModal.colMetric')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('safetyStock.parameters.sampleModal.colCurrent')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('safetyStock.parameters.sampleModal.colSimulated')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('safetyStock.parameters.sampleModal.colDelta')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-3 py-2 text-gray-600">
                        {t('safetyStock.parameters.sampleModal.metricUnits')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {formatUnits(prevUnits, locale)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
                        {formatUnits(units, locale)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-medium ${deltaTextClass(
                          prevUnits && prevUnits > 0 && deltaUnits != null
                            ? deltaUnits / prevUnits
                            : null,
                        )}`}
                      >
                        {formatSignedUnits(deltaUnits, locale)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-600">
                        {t('safetyStock.parameters.sampleModal.metricValue', { currencySymbol: symbol })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {prevValue == null ? '—' : formatRounded(prevValue)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
                        {value == null ? '—' : formatRounded(value)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-medium ${deltaTextClass(deltaValuePct)}`}
                      >
                        <div>{formatSignedMoney(deltaValue, formatRounded)}</div>
                        {deltaValuePct != null ? (
                          <div className="text-xs font-normal">
                            {formatSignedPercentFraction(deltaValuePct, locale)}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-600">
                        {t('safetyStock.parameters.sampleModal.metricDays')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {formatDays(prevDays, locale, 1)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
                        {formatDays(simDays, locale, 1)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-medium ${deltaTextClass(
                          prevDays && prevDays > 0 && deltaDays != null
                            ? deltaDays / prevDays
                            : null,
                        )}`}
                      >
                        {deltaDays == null
                          ? '—'
                          : `${deltaDays > 0 ? '+' : ''}${formatDays(deltaDays, locale, 1)}`}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-gray-500">
                {t('safetyStock.parameters.sampleModal.resultDisclaimer')}
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 px-5 py-3">
          {mode === 'confirm' ? (
            <>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                onClick={onClose}
              >
                {t('safetyStock.apply.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                onClick={onConfirm}
              >
                {t('safetyStock.parameters.sampleModal.confirmCta')}
              </button>
            </>
          ) : null}
          {mode === 'error' ? (
            <>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                onClick={onClose}
              >
                {t('safetyStock.apply.close')}
              </button>
              <button
                type="button"
                className="rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                onClick={onRetry}
              >
                {t('safetyStock.apply.retry')}
              </button>
            </>
          ) : null}
          {mode === 'result' ? (
            <button
              type="button"
              className="rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
              onClick={onClose}
            >
              {t('safetyStock.apply.close')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
